import { IllustrationTree } from '@/components/IllustrationTree';
import { ListItem } from '@/components/ListItem';
import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { Button, Link, List, Stack, Typography } from '@mui/material';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { Trans, useTranslation } from 'react-i18next';

interface DisclaimerStepProps {
  handleNextStep: () => void;
  onNext: boolean;
}

export const DisclaimerStep = (props: DisclaimerStepProps) => {
  const { handleNextStep, onNext } = props;
  const { t } = useTranslation();
  return (
    <WizardAnimationWrapper onNext={onNext} firstStep>
      <StepLayout>
        <Row height="100%">
          <Col xs={12} md lg="auto">
            <Stack gap={4}>
              <Stack gap={3} marginX={{ xs: 'auto', md: 0 }} maxWidth={640}>
                <Typography variant="h4">
                  {t('is_treyd_right_for_you')}
                </Typography>
                <Stack gap={2}>
                  <Stack gap={1}>
                    <Typography variant="body1">
                      {t('treyd_is_built_for_businesses_like_yours_if_you')}
                    </Typography>
                    <List
                      disablePadding
                      sx={{ display: 'flex', flexDirection: 'column' }}>
                      <ListItem
                        title={t('have_a_limited_company')}
                        subtitle={t('disclaimer.finance.list.item1.sub')}
                      />
                      <ListItem title={t('sell_physical_goods')} />
                      <ListItem
                        title={t('generate_20000_in_monthly_revenue')}
                      />
                      <ListItem
                        title={t('have_been_in_business_for_at_least_1_year')}
                      />
                    </List>
                  </Stack>
                  <Stack gap={1}>
                    <Typography variant="subtitle1">
                      {t('what_we_finance')}
                    </Typography>
                    <List
                      disablePadding
                      sx={{ display: 'flex', flexDirection: 'column' }}>
                      <ListItem
                        title={t(
                          'we_fund_supplier_invoices_and_outstanding_customer_invoices_covering_expenses_directly_tied_to_your_supply_chain_this_includes_finished_goods_raw_materials_packaging_and_services_like_freight_duty_fulfillment_digital_marketing_licensing_insurance'
                        )}
                      />
                    </List>
                  </Stack>
                  <Stack gap={1}>
                    <Typography variant="subtitle1">
                      {t('what_we_dont_finance')}
                    </Typography>
                    <List
                      disablePadding
                      sx={{ display: 'flex', flexDirection: 'column' }}>
                      <ListItem
                        title={
                          <Trans
                            i18nKey={
                              'wages_rent_or_other_internal_expenses_see_full_exclusion_list_here'
                            }
                            components={{
                              info: (
                                <Link
                                  href={
                                    'https://www.treyd.io/credit-exclusions/'
                                  }
                                  target="_blank"
                                  style={{ textDecoration: 'none' }}>
                                  <Typography
                                    variant="body1"
                                    color="info.main"
                                    display="inline"
                                  />{' '}
                                </Link>
                              ),
                            }}
                          />
                        }
                        forbid
                      />
                    </List>
                  </Stack>

                  <Typography
                    variant="body1"
                    color="text.primary"
                    display="inline">
                    {t(
                      'if_you_have_any_questions_our_support_team_is_available_please_use_the_chat_in_the_corner_to_reach_out'
                    )}
                  </Typography>
                </Stack>
              </Stack>
              <Button
                variant="contained"
                endIcon={<ArrowRightIcon />}
                size="large"
                sx={{ width: { xs: '100%', md: 'fit-content' } }}
                onClick={handleNextStep}>
                {t('disclaimer.action')}
              </Button>
            </Stack>
          </Col>
          <Col xs display={{ xs: 'none', md: 'unset' }}>
            <Stack justifyContent="center" alignItems="center" height="100%">
              <IllustrationTree
                style={{ fill: 'none' }}
                sx={{
                  color: 'grey.900',
                  width: 280,
                  height: 391,
                }}
                viewBox="0 0 280 391"
              />
            </Stack>
          </Col>
        </Row>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};
